import React, {useEffect, useMemo} from 'react';

import {AsyncDataLoader} from '../../components/common/loaders/asyncDataLoader';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {computeIsGlobalNamespace, isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {SettingsResourcePagesEnum, SettingsRulesUrl} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, SettingsTrackingTypesEnum, trackInterfaceEvent} from '../../services/tracking';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {RuleLibrary} from './library/ruleLibrary';
import {RuleList} from './list/ruleList';
import {buildRuleEditorBreadcrumbs} from './ruleEditor/ruleEditorBreadcrumbs';
import {
  createRuleEditorServiceContext,
  RuleEditorServiceContext,
} from './ruleEditor/ruleEditorServiceContext';
import {RuleEditorWrapper} from './ruleEditorWrapper';
import {useRuleIntegrations} from './ruleIntegrationModel';

/*
 * Types.
 */

interface SettingsRulesContainerProps {
  panelId: SettingsPanelId;
  url: SettingsRulesUrl;
}

/*
 * Component.
 */

export const SettingsRulesContainer = ({panelId, url}: SettingsRulesContainerProps) => {
  const {resourcePage, namespace, ruleId, recipe: recipeId, bundleId, isInboxCreation, inboxId} = url;
  const editorServiceContext = useMemo(() => createRuleEditorServiceContext(), []);
  const {navigateToLink} = useLinkNavigation();
  const ruleIntegrationsAsyncData = useRuleIntegrations();

  const breadcrumbs = useMemo(
    () => buildRuleEditorBreadcrumbs(namespace, Boolean(recipeId), isInboxCreation, navigateToLink),
    [namespace, recipeId, navigateToLink],
  );

  useEffect(() => {
    const subPage = resourcePageToSubPage(resourcePage);

    if (subPage === undefined) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_PAGE_VIEW, {
      settings_page: 'rules',
      settings_type: getSettingTypeFromNamespace(namespace),
      namespace,
      settings_subpage: subPage,
    });
  }, [namespace, resourcePage]);

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return <RuleList rulesUrl={url} />;
  }

  if (resourcePage === SettingsResourcePagesEnum.LIBRARY) {
    return (
      <AsyncDataLoader data={ruleIntegrationsAsyncData}>
        {(ruleIntegration) => <RuleLibrary rulesUrl={url} integrations={ruleIntegration} />}
      </AsyncDataLoader>
    );
  }

  return (
    <RuleEditorServiceContext.Provider value={editorServiceContext}>
      <AsyncDataLoader data={ruleIntegrationsAsyncData}>
        {(ruleIntegration) => (
          <RuleEditorWrapper
            panelId={panelId}
            ruleIntegration={ruleIntegration}
            resourcePage={resourcePage}
            namespace={namespace}
            ruleId={ruleId}
            recipeId={recipeId}
            breadcrumbs={breadcrumbs}
            bundleId={bundleId}
            selectedInboxId={isInboxCreation ? inboxId : undefined}
          />
        )}
      </AsyncDataLoader>
    </RuleEditorServiceContext.Provider>
  );
};

/*
 * Helpers.
 */

// From front:public/js/common/settings_tracking.js
function resourcePageToSubPage(resourcePage: SettingsResourcePagesEnum) {
  switch (resourcePage) {
    case SettingsResourcePagesEnum.NEW:
      return 'Add a rule';

    case SettingsResourcePagesEnum.EDIT:
      return 'Edit an existing rule';

    case SettingsResourcePagesEnum.LIBRARY:
      return 'Rules library';

    case SettingsResourcePagesEnum.OVERVIEW:
      return 'All rules';

    default:
      return undefined;
  }
}

function getSettingTypeFromNamespace(namespace: string) {
  if (computeIsGlobalNamespace(namespace)) {
    return SettingsTrackingTypesEnum.COMPANY;
  }

  if (isTeamNamespace(namespace)) {
    return SettingsTrackingTypesEnum.TEAM;
  }

  return SettingsTrackingTypesEnum.PERSONAL;
}
